import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  Loader2,
  AlertCircle,
  Phone,
  Bell,
  BellRing,
  ChefHat,
  Package,
  Truck,
  XCircle,
  ArrowLeft,
  RefreshCw,
  ShoppingBag,
  History,
} from "lucide-react";
import { toast } from "sonner";

import { deliveryApi } from "../services/deliveryApi";
import {
  isPushSupported,
  subscribeToOrderPush,
} from "../utils/deliveryClientPush";

const PASSOS = [
  { key: "recebido", label: "Pedido recebido", icon: Bell },
  { key: "aceito", label: "Aceito", icon: CheckCircle2 },
  { key: "em_preparo", label: "Em preparo", icon: ChefHat },
  { key: "pronto", label: "Pronto", icon: Package },
  { key: "entregue", label: "Entregue", icon: Truck },
];

const formatBRL = (v) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

export default function DeliveryPedidoTrack() {
  const { numero } = useParams();
  const navigate = useNavigate();
  const [pedido, setPedido] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushActive, setPushActive] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await deliveryApi.trackPedido(numero);
      setPedido(r);
      setPushActive(Boolean(r?.cliente?.has_push_subscription));
      setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Pedido não encontrado");
    } finally {
      setLoading(false);
    }
  }, [numero]);

  const handleAtivarPush = useCallback(async () => {
    if (!pedido?.numero_curto) return;
    setPushBusy(true);
    try {
      const r = await subscribeToOrderPush(pedido.numero_curto);
      if (r.ok) {
        setPushActive(true);
        toast.success("Notificações ativadas — avisaremos quando o status mudar 🔔");
      } else if (r.reason === "denied") {
        toast.error("Permissão negada. Ajuste as notificações do navegador.");
      } else if (r.reason === "server_unconfigured") {
        toast.error("Notificações indisponíveis no momento.");
      } else if (r.reason === "sw_unavailable") {
        toast.info("Notificações disponíveis apenas no app instalado.");
      } else if (r.reason === "unsupported") {
        toast.info("Seu navegador não suporta notificações push.");
      } else {
        toast.error("Não foi possível ativar agora — tente novamente.");
      }
    } finally {
      setPushBusy(false);
    }
  }, [pedido]);

  useEffect(() => {
    load();
  }, [load]);

  // Auto refresh enquanto pedido está em andamento
  useEffect(() => {
    if (!pedido) return;
    if (pedido.status === "entregue" || pedido.status === "cancelado") return;
    const t = setInterval(load, 25000);
    return () => clearInterval(t);
  }, [pedido, load]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--primary)]" />
      </div>
    );
  }

  if (err || !pedido) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-12 h-12 mx-auto text-red-500 mb-3" />
          <h2 className="text-lg font-semibold mb-2">{err || "Indisponível"}</h2>
          <Link to="/" className="text-sm text-[var(--primary)] underline">
            Voltar para o início
          </Link>
        </div>
      </div>
    );
  }

  const isCancelado = pedido.status === "cancelado";
  const idxAtual = PASSOS.findIndex((p) => p.key === pedido.status);
  const historicoMap = {};
  (pedido.historico_status || []).forEach((h) => {
    if (!historicoMap[h.status]) historicoMap[h.status] = h.timestamp;
  });
  const loja = pedido.loja || {};
  const cardapioUrl = loja.negocio_slug && pedido.tipo
    ? `/delivery/${(pedido.municipio_slug || "")}` // best-effort
    : null;

  return (
    <div className="min-h-screen bg-[var(--bg-secondary)] pb-12">
      {/* Hero */}
      <div className={`text-white ${isCancelado ? "bg-gradient-to-br from-red-600 to-red-700" : "bg-gradient-to-br from-orange-500 to-red-500"}`}>
        <div className="max-w-md mx-auto px-4 py-6">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs opacity-90"
            data-testid="track-back"
          >
            <ArrowLeft className="w-3 h-3" /> Início
          </Link>
          <div className="flex items-center justify-between mt-2">
            <div>
              <p className="text-xs opacity-90">Pedido</p>
              <h1 className="text-2xl font-bold font-mono" data-testid="track-numero">
                {pedido.numero_curto}
              </h1>
            </div>
            <button
              onClick={load}
              className="p-2 bg-white/10 hover:bg-white/20 rounded-lg"
              title="Atualizar status"
              data-testid="track-refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm opacity-90 mt-1">{loja.nome_loja || "Loja"}</p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 py-6">
        {/* Status atual destacado */}
        <div className="bg-white rounded-2xl border border-[var(--border)] p-4 shadow-sm mb-4">
          {pedido.cliente?.pedidos_count > 1 && (
            <div
              className="mb-3 px-3 py-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg text-xs text-amber-800"
              data-testid="track-recurrent-badge"
            >
              🏆 Este é o seu <strong>{pedido.cliente.pedidos_count}º pedido</strong> nesta loja — obrigado pela preferência!
            </div>
          )}
          {isCancelado ? (
            <div className="flex items-center gap-3" data-testid="track-status-cancelado">
              <XCircle className="w-8 h-8 text-red-500" />
              <div>
                <h2 className="font-bold text-red-600">Pedido cancelado</h2>
                <p className="text-xs text-[var(--text-secondary)]">
                  Em {fmtTime(pedido.updated_at)}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-3" data-testid={`track-status-${pedido.status}`}>
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-white flex items-center justify-center">
                {React.createElement(PASSOS[idxAtual]?.icon || Clock, { className: "w-6 h-6" })}
              </div>
              <div>
                <h2 className="font-bold">{PASSOS[idxAtual]?.label || "Em andamento"}</h2>
                <p className="text-xs text-[var(--text-secondary)]">
                  Atualizado às {fmtTime(pedido.updated_at)}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Stepper */}
        {!isCancelado && (
          <div className="bg-white rounded-2xl border border-[var(--border)] p-4 shadow-sm mb-4" data-testid="track-stepper">
            <ol className="relative">
              {PASSOS.map((p, i) => {
                const Icon = p.icon;
                const done = i <= idxAtual;
                const isCurrent = i === idxAtual;
                const ts = historicoMap[p.key];
                return (
                  <li key={p.key} className="flex items-start gap-3 pb-3 last:pb-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                        done
                          ? isCurrent
                            ? "bg-orange-500 text-white"
                            : "bg-emerald-500 text-white"
                          : "bg-[var(--bg-secondary)] text-[var(--text-secondary)]"
                      }`}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 -mt-0.5">
                      <p className={`text-sm ${done ? "font-medium" : "text-[var(--text-secondary)]"}`}>
                        {p.label}
                      </p>
                      {ts && (
                        <p className="text-xs text-[var(--text-secondary)]">{fmtTime(ts)}</p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        {/* Itens */}
        <div className="bg-white rounded-2xl border border-[var(--border)] p-4 shadow-sm mb-4">
          <h3 className="text-sm font-semibold mb-2">Seu pedido</h3>
          <ul className="space-y-1 text-sm">
            {pedido.itens.map((it, i) => (
              <li key={i} className="flex justify-between">
                <span>
                  <span className="font-semibold">{it.quantidade}×</span> {it.nome}
                </span>
                <span className="text-[var(--text-secondary)]">{formatBRL(it.subtotal)}</span>
              </li>
            ))}
          </ul>
          <div className="border-t border-[var(--border)] mt-3 pt-2 text-sm space-y-1">
            <div className="flex justify-between text-[var(--text-secondary)]">
              <span>Subtotal</span>
              <span>{formatBRL(pedido.subtotal)}</span>
            </div>
            <div className="flex justify-between text-[var(--text-secondary)]">
              <span>Taxa de entrega</span>
              <span>{formatBRL(pedido.taxa_entrega)}</span>
            </div>
            <div className="flex justify-between font-bold pt-1">
              <span>Total</span>
              <span data-testid="track-total">{formatBRL(pedido.total)}</span>
            </div>
            <div className="text-xs text-[var(--text-secondary)] pt-1">
              Pagamento: <strong>{pedido.forma_pagamento}</strong> ·
              {" "}{pedido.tipo === "entrega" ? "Entrega" : "Retirada"}
            </div>
          </div>
        </div>

        {/* Contato com loja */}
        {loja.telefone && (
          <a
            href={`https://wa.me/55${String(loja.telefone).replace(/\D/g, "")}?text=Ol%C3%A1!%20Tenho%20uma%20d%C3%BAvida%20sobre%20o%20pedido%20${pedido.numero_curto}`}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full text-center px-4 py-3 bg-emerald-500 text-white rounded-xl font-medium hover:bg-emerald-600"
            data-testid="track-whatsapp"
          >
            <Phone className="w-4 h-4 inline mr-2" />
            Falar com {loja.nome_loja} no WhatsApp
          </a>
        )}

        {/* CTA — Ativar notificações push (P3 fev/2026) */}
        {!isCancelado && pedido.status !== "entregue" && isPushSupported() && (
          <div
            className="mt-4 bg-white rounded-2xl border border-[var(--border)] p-4 shadow-sm"
            data-testid="track-push-card"
          >
            {pushActive ? (
              <div className="flex items-center gap-3 text-sm">
                <div className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                  <BellRing className="w-4 h-4" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[var(--text-primary)]">
                    Notificações ativadas
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Vamos te avisar quando o status mudar.
                  </p>
                </div>
              </div>
            ) : (
              <button
                onClick={handleAtivarPush}
                disabled={pushBusy}
                className="w-full flex items-center gap-3 px-1 py-1 text-left disabled:opacity-60"
                data-testid="track-push-enable"
              >
                <div className="w-9 h-9 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center shrink-0">
                  <Bell className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm text-[var(--text-primary)]">
                    Receba avisos quando o status mudar
                  </p>
                  <p className="text-xs text-[var(--text-secondary)]">
                    Toque para ativar notificações neste dispositivo.
                  </p>
                </div>
                <span className="px-3 py-1.5 text-xs font-semibold bg-[var(--primary)] text-white rounded-lg shrink-0">
                  {pushBusy ? "..." : "Ativar"}
                </span>
              </button>
            )}
          </div>
        )}

        {/* Histórico recente do mesmo cliente */}
        {pedido.historico_recente && pedido.historico_recente.length > 0 && (
          <div
            className="bg-white rounded-2xl border border-[var(--border)] p-4 shadow-sm mt-4"
            data-testid="track-historico"
          >
            <h3 className="text-sm font-semibold mb-3 flex items-center gap-2">
              <History className="w-4 h-4 text-[var(--primary)]" /> Seus pedidos anteriores aqui
            </h3>
            <ul className="space-y-2">
              {pedido.historico_recente.map((h) => (
                <li
                  key={h.id}
                  className="flex items-start gap-3 pb-2 border-b border-[var(--border)] last:border-b-0 last:pb-0"
                  data-testid={`track-historico-item-${h.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-xs">{h.numero_curto}</span>
                      <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded">
                        {h.status}
                      </span>
                      <span className="text-xs text-[var(--text-secondary)]">
                        {fmtTime(h.created_at)}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--text-secondary)] truncate mt-0.5">
                      {(h.itens_resumo || []).map((it) => `${it.quantidade}× ${it.nome}`).join(", ")}
                    </p>
                    <p className="text-xs font-semibold text-[var(--primary)] mt-0.5">
                      {formatBRL(h.total)}
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      try {
                        sessionStorage.setItem(
                          `dlv-reorder-${loja.negocio_id}`,
                          JSON.stringify({
                            ts: Date.now(),
                            itens: h.itens_resumo,
                            ref_pedido: h.numero_curto,
                          })
                        );
                      } catch {
                        // ignore
                      }
                      if (loja.municipio_slug && loja.negocio_slug) {
                        navigate(`/delivery/${loja.municipio_slug}/${loja.negocio_slug}`);
                      } else if (loja.negocio_id) {
                        navigate(`/negocio/${loja.negocio_id}`);
                      }
                    }}
                    className="px-2 py-1 text-xs bg-[var(--primary)] text-white rounded-lg hover:opacity-90 inline-flex items-center gap-1 shrink-0"
                    data-testid={`track-reorder-${h.id}`}
                  >
                    <ShoppingBag className="w-3 h-3" /> Pedir igual
                  </button>
                </li>
              ))}
            </ul>
            <p className="text-[10px] text-[var(--text-secondary)] mt-3 text-center">
              Clique em "Pedir igual" e adicionamos os itens ao seu carrinho automaticamente.
            </p>
          </div>
        )}

        {loja.negocio_slug && (
          <Link
            to={`/negocio/${loja.negocio_id || ""}`}
            className="block text-center mt-3 text-xs text-[var(--text-secondary)] underline"
          >
            Ver perfil da loja
          </Link>
        )}
      </div>
    </div>
  );
}
